import { useCallback, useMemo, useState } from 'react'
import { ClipboardCheck, Gavel, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { DataTable } from '@/admin/components/data-table'
import type { DataTableColumn } from '@/admin/components/data-table'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import useForm from '@/shared/lib/forms/useForm'
import type { FormField } from '@/shared/lib/forms'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useAssessmentLineItems,
  useAssessmentOptions,
  useBulkDecideAssessment,
  useCreateAssessmentLineItem,
  useDecideAssessmentLineItem,
  useDeleteAssessmentLineItem,
  useUpdateAssessmentLineItem,
  type AssessmentLineItem,
} from '@/admin/hooks/use-assessment'
import { useClaimChildren, usePricingGuidance } from '@/admin/hooks/use-claims'
import { PricingGuidancePanel } from '@/admin/pages/claims/pricing-guidance-panel'

interface Props {
  claimUuid: string
  /** Whether the adjudicator may edit rows (the claim is in the assessment stage). */
  editable?: boolean
}

/**
 * Per-item assessment working-out for an umbrella (multi-item) claim: one row
 * per item with a coverage decision + amount. Internal-only - the endpoints it
 * reads require ``claims.view_decisions`` server-side; this component is also
 * rendered behind that gate. Coverage-decision options come from the API
 * (``/claims/assessment/options/``), never a hardcoded array.
 */
export function AssessmentLineItemsTable({ claimUuid, editable = false }: Props) {
  const { formatCurrency } = useFormatting()
  const {
    data: lineItems = [],
    isLoading,
    isError,
  } = useAssessmentLineItems(claimUuid)
  const { data: options, isLoading: isLoadingOptions } = useAssessmentOptions()
  const coverageOptions = useMemo(
    () => options?.coverage_decisions ?? [],
    [options],
  )
  // The parent's child claims - the assessment rows are one per item, so the
  // editor picks the item from this list (links source_child_claim) rather than
  // typing a free-text label, which is what makes the row decidable.
  const { data: children = [] } = useClaimChildren(claimUuid)

  const createMut = useCreateAssessmentLineItem(claimUuid)
  const updateMut = useUpdateAssessmentLineItem(claimUuid)
  const deleteMut = useDeleteAssessmentLineItem(claimUuid)
  const decideMut = useDecideAssessmentLineItem(claimUuid)
  const bulkDecideMut = useBulkDecideAssessment(claimUuid)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AssessmentLineItem | null>(null)
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  // The item whose reference pricing shows in the dialog - so the adjudicator
  // can enter/sanity-check the amount against the product's guidance.
  const [selectedChildUuid, setSelectedChildUuid] = useState<string | null>(null)
  const { data: pricingGuidance } = usePricingGuidance(
    dialogOpen ? selectedChildUuid : null,
  )

  const total = useMemo(
    () => lineItems.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [lineItems],
  )

  const coverageLabel = useCallback(
    (value: string | null | undefined) =>
      coverageOptions.find((o) => o.value === value)?.label ?? value ?? '-',
    [coverageOptions],
  )

  const handleOpenCreate = useCallback(() => {
    setEditing(null)
    setSelectedChildUuid(null)
    setDialogOpen(true)
  }, [])

  const handleOpenEdit = useCallback((row: AssessmentLineItem) => {
    setEditing(row)
    setSelectedChildUuid(row.source_child_claim ?? null)
    setDialogOpen(true)
  }, [])

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false)
    setEditing(null)
    setSelectedChildUuid(null)
  }, [])

  const handleDelete = useCallback(
    (uuid: string) => {
      deleteMut.mutate(uuid, {
        onSuccess: () => {
          toast.success('Assessment row removed')
          setDeletingUuid(null)
        },
        onError: (err) => toastApiError(err, 'Failed to remove row'),
      })
    },
    [deleteMut],
  )

  // Commit one row: advances its child claim per the coverage decision.
  const handleDecide = useCallback(
    (row: AssessmentLineItem) => {
      decideMut.mutate(row.uuid, {
        onSuccess: () => toast.success('Item decision applied'),
        onError: (err) => toastApiError(err, 'Failed to decide item'),
      })
    },
    [decideMut],
  )

  // Rows that can still be committed (decision set, not locked, child open).
  const committableCount = useMemo(
    () =>
      lineItems.filter(
        (r) => !r.is_locked && !r.child_resolved && !!r.coverage_decision,
      ).length,
    [lineItems],
  )
  // Progress toward completing the assessment (all children decided).
  const childRowCount = useMemo(
    () => lineItems.filter((r) => !!r.source_child_claim).length,
    [lineItems],
  )
  const resolvedCount = useMemo(
    () => lineItems.filter((r) => r.child_resolved).length,
    [lineItems],
  )

  const handleDecideAll = useCallback(() => {
    bulkDecideMut.mutate(undefined, {
      onSuccess: (res) => {
        const decided = res?.summary?.decided?.length ?? 0
        const skipped = res?.summary?.skipped?.length ?? 0
        const failed = res?.summary?.errors?.length ?? 0
        toast.success(
          `Decided ${decided} item(s)` +
            (skipped ? `, ${skipped} skipped` : '') +
            (failed ? `, ${failed} failed` : ''),
        )
      },
      onError: (err) => toastApiError(err, 'Failed to decide items'),
    })
  }, [bulkDecideMut])

  const columns = useMemo<DataTableColumn<AssessmentLineItem>[]>(() => {
    const cols: DataTableColumn<AssessmentLineItem>[] = [
      { id: 'description', header: 'Item', accessorKey: 'description' },
      {
        id: 'coverage_decision',
        header: 'Coverage',
        cell: (row) =>
          row.coverage_decision ? (
            <Badge variant="outline">{coverageLabel(row.coverage_decision)}</Badge>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: 'amount',
        header: 'Amount',
        cell: (row) => formatCurrency(Number(row.amount || 0)),
      },
      {
        id: 'child_state',
        header: 'State',
        cell: (row) =>
          row.child_state ? (
            <Badge variant={row.child_resolved ? 'secondary' : 'outline'}>
              {row.child_state}
            </Badge>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
    ]
    if (editable) {
      cols.push({
        id: 'actions',
        header: '',
        cell: (row) => {
          if (row.is_locked) {
            return <span className="text-[12px] text-[var(--ink-3)]">Locked</span>
          }
          // Child already decided/rejected - its decision is final; no editing.
          if (row.child_resolved) {
            return <span className="text-[12px] text-[var(--ink-3)]">-</span>
          }
          return (
            <div className="flex items-center justify-end gap-1">
              {row.coverage_decision && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  title="Commit decision (advance this item)"
                  disabled={decideMut.isPending}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDecide(row)
                  }}
                >
                  <Gavel size={14} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenEdit(row)
                }}
              >
                <Pencil size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeletingUuid(row.uuid)
                }}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          )
        },
      })
    }
    return cols
  }, [
    coverageLabel,
    formatCurrency,
    editable,
    handleOpenEdit,
    handleDecide,
    decideMut.isPending,
  ])

  // Item options: children not already linked to a row, plus the one being
  // edited (so an existing row still shows its item). Short id disambiguates
  // identical products.
  const childOptions = useMemo(() => {
    const linkedElsewhere = new Set(
      lineItems
        .map((r) => r.source_child_claim)
        .filter(
          (id): id is string => !!id && id !== editing?.source_child_claim,
        ),
    )
    return children
      .filter((c) => !linkedElsewhere.has(c.uuid))
      .map((c) => ({
        value: c.uuid,
        label: `${c.product_name || 'Item'} (#${c.uuid.slice(0, 6).toUpperCase()})`,
      }))
  }, [children, lineItems, editing])

  // Children with no row yet - what "Add Item" can still add.
  const unlinkedChildCount = useMemo(() => {
    const linked = new Set(
      lineItems.map((r) => r.source_child_claim).filter(Boolean),
    )
    return children.filter((c) => !linked.has(c.uuid)).length
  }, [children, lineItems])

  const initial: Partial<AssessmentLineItem> =
    editing ?? {
      source_child_claim: null,
      coverage_decision: '',
      amount: 0,
      notes: '',
    }
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'source_child_claim',
        label: 'Item',
        value: (initial.source_child_claim as string) ?? '',
        options: childOptions,
        required: 'Select an item',
        className: 'col-span-2',
        onChange: (value) => setSelectedChildUuid((value as string) || null),
      },
      {
        type: 'select',
        name: 'coverage_decision',
        label: 'Coverage Decision',
        value: (initial.coverage_decision as string) ?? '',
        options: coverageOptions.map((o) => ({ value: o.value, label: o.label })),
        className: 'col-span-1',
      },
      {
        type: 'number',
        name: 'amount',
        label: 'Amount',
        value: initial.amount,
        className: 'col-span-1',
      },
      {
        type: 'textarea',
        name: 'notes',
        label: 'Notes',
        value: initial.notes,
        className: 'col-span-3',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogOpen, editing?.uuid, coverageOptions, childOptions],
  )

  const { Form, ref } = useForm(
    { name: `assessment-line-item-${editing ? 'edit' : 'create'}`, submit: 'footer', fields },
    async (rawValues) => {
      const v = rawValues as Record<string, unknown>
      const childUuid = (v.source_child_claim as string) || ''
      const child = children.find((c) => c.uuid === childUuid)
      const payload: Record<string, unknown> = {
        // Description rides along for display; the child link is what matters.
        description: child?.product_name || editing?.description || 'Item',
        source_child_claim: childUuid || null,
        coverage_decision: v.coverage_decision || null,
        amount: v.amount,
        notes: ((v.notes as string) ?? '').trim(),
      }
      try {
        if (editing) {
          await updateMut.mutateAsync({ itemUuid: editing.uuid, data: payload })
          toast.success('Assessment row updated')
        } else {
          await createMut.mutateAsync(payload)
          toast.success('Assessment row added')
        }
        handleDialogClose()
      } catch (err) {
        toastApiError(err, `Failed to ${editing ? 'update' : 'add'} row`)
      }
    },
    [dialogOpen, editing?.uuid],
  )

  const busy = createMut.isPending || updateMut.isPending

  return (
    <section className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
      <div className="flex flex-col gap-5 p-5 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-[var(--ink-3)]" />
            <h3 className="text-[17px] font-bold leading-[1.2] tracking-[-0.01em] text-[var(--ink)] sm:text-[19px] font-display !font-bold">
              Assessment
            </h3>
          </div>
          {editable && (
            <div className="flex items-center gap-2">
              {committableCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDecideAll}
                  disabled={bulkDecideMut.isPending}
                >
                  <Gavel size={14} className="mr-1" />
                  {bulkDecideMut.isPending
                    ? 'Deciding…'
                    : `Decide all (${committableCount})`}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenCreate}
                disabled={unlinkedChildCount === 0}
                title={
                  unlinkedChildCount === 0
                    ? 'Every item already has an assessment row'
                    : undefined
                }
              >
                <Plus size={14} className="mr-1" />
                Add Item
              </Button>
            </div>
          )}
        </div>

        {isLoading || isLoadingOptions ? (
          <div className="flex items-center justify-center py-6 text-[13px] font-light text-[var(--ink-3)]">
            Loading assessment items...
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-6 text-[13px] font-light text-[var(--status-error)]">
            Failed to load assessment items.
          </div>
        ) : lineItems.length > 0 ? (
          <>
            <DataTable
              tableId="assessment-line-items"
              columns={columns}
              data={lineItems}
              rowKey="uuid"
              emptyMessage="No items to assess yet"
            />
            <div className="flex items-center justify-between gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-3">
              {childRowCount > 0 ? (
                <span className="text-[13px] font-light text-[var(--ink-3)]">
                  {resolvedCount} of {childRowCount} items decided
                  {resolvedCount >= childRowCount
                    ? ' - ready to complete'
                    : ''}
                </span>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-3">
                <span className="text-[13px] font-light text-[var(--ink-3)]">
                  Assessed total
                </span>
                <span className="text-[15px] font-medium text-[var(--ink)]">
                  {formatCurrency(total)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-[13px] font-light text-[var(--ink-3)]">
            No items to assess yet.{' '}
            {editable && 'Click "Add Item" to record a coverage decision.'}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Assessment Row' : 'Add Assessment Row'}</DialogTitle>
            <DialogDescription>
              Record the coverage decision and assessed amount for this item.
            </DialogDescription>
          </DialogHeader>
          <Form className="grid grid-cols-3 gap-x-4 gap-y-3 py-2" />
          {selectedChildUuid && (
            <div className="pb-1">
              <PricingGuidancePanel guidance={pricingGuidance} showEmptyNote />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleDialogClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => ref.current?.submitForm()} disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deletingUuid !== null}
        onOpenChange={(open) => !open && setDeletingUuid(null)}
        onConfirm={() => deletingUuid && handleDelete(deletingUuid)}
        title="Remove assessment row?"
        description="This removes the row from the assessment working-out."
        isPending={deleteMut.isPending}
      />
    </section>
  )
}
